import { useHasCatalogFeature } from "../../../hooks/useHasCatalogFeature";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import type { TableMeta } from "@tanstack/react-table";
import type {
  ProductGroupId,
  ProductId,
  StockLocationId,
} from "@KassaroAB/shared/types/ids";
import { Features } from "@KassaroAB/shared/types/enums/Features";
import { CatalogDomain } from "@KassaroAB/shared/types/enums/CatalogDomain";
import { CatalogOrigin } from "@KassaroAB/shared/types/enums/CatalogOrigin";
import { Permissions } from "@KassaroAB/shared/types/enums/Permissions";
import { defaultCatalogPolicy } from "@KassaroAB/shared/types/catalogPolicy";
import {
  hasChainCatalogAccess,
  isFieldOverridable,
  resolveDomainPolicy,
} from "@KassaroAB/shared/utils/catalogPolicy";
import {
  ProductOverlayFieldScheme,
  type ProductOverlayField,
} from "@KassaroAB/shared/types/productOverlay";
import type {
  RestProduct,
  RestUpdateProduct,
} from "@KassaroAB/shared/types/product";
import type { RestProductGroup } from "@KassaroAB/shared/types/productGroup";
import type { ProductStore } from "../../../data/productStore";
import type {
  ProductStoreStatus,
  ProductSyncProgress,
} from "../../../hooks/useProductStore";
import type { CatalogScope } from "../../../api/catalogScope";
import { Country } from "@KassaroAB/shared/types/enums/Country";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import { useGetBookkeepingAccountsQuery } from "../../../query/bookkeepingAccountsQuery";
import { useShowCatalogOriginMarkers } from "../../../hooks/useShowCatalogOriginMarkers";
import { useGetChainQuery } from "../../../query/chainsQuery";
import { useGetCoursesQuery } from "../../../query/coursesQuery";
import { useGetPrepStationsQuery } from "../../../query/prepStationsQuery";
import { useGetProductOverlaysQuery } from "../../../query/productOverlaysQuery";
import { useGetStorePermissionsQuery } from "../../../query/storePermissionsQuery";
import { useGetUserQuery } from "../../../query/usersQuery";
import { useGetProductGroupsQuery } from "../../../query/productGroupsQuery";
import { useGetStoreQuery } from "../../../query/storesQuery";
import { useGetUnitsQuery } from "../../../query/unitsQuery";
import { useGetStockLocationsQuery } from "../../../query/stockLocationsQuery";
import { useGetStockPoliciesQuery } from "../../../query/stockPoliciesQuery";
import { useGetStockWarningsQuery } from "../../../query/stockWarningsQuery";
import { useGetSuppliersQuery } from "../../../query/suppliersQuery";
import { DateTime } from "luxon";
import { StockWarningType } from "@KassaroAB/shared/types/enums/StockWarningType";
import { evaluateStock } from "@KassaroAB/shared/utils/stockPolicy";
import { applyChainValues } from "@KassaroAB/shared/catalog/productOverlay";
import {
  buildProductColumnLabels,
  createProductColumns,
  restrictOverlayChanges,
  type ChainProductSaveTarget,
  type ProductOverlayMeta,
  type ProductTableMeta,
  type StockStatus,
} from "./productTableColumns";
import {
  ProductsTableContext,
  type ProductsTableContextValue,
} from "./productsTableContext";
import { useProductTableEdits } from "./useProductTableEdits";
import { useProductTableFilters } from "./useProductTableFilters";
import { useProductTableQuery } from "./useProductTableQuery";
import { useProductTableStock } from "./useProductTableStock";

interface ProductsTableProviderProps {
  scope: CatalogScope | null;
  storeRef: MutableRefObject<ProductStore | null>;
  status: ProductStoreStatus;
  version: number;
  applyUpdated: (products: RestProduct[]) => Promise<void>;
  canEdit: boolean;
  onEditProduct: (product: RestProduct) => void;
  resync: () => Promise<void>;
  forceResync: () => Promise<void>;
  lastSyncedAt: Date | null;
  syncProgress: ProductSyncProgress | null;
  toolbarActions?: ReactNode;
  deleteProducts: (products: RestProduct[]) => Promise<void>;
  children: ReactNode;
}

export const ProductsTableProvider = ({
  scope,
  storeRef,
  status,
  version,
  applyUpdated,
  canEdit,
  onEditProduct,
  resync,
  forceResync,
  lastSyncedAt,
  syncProgress,
  toolbarActions,
  deleteProducts,
  children,
}: ProductsTableProviderProps) => {
  const { t } = useTranslation();
  const storeId = scope?.type === "store" ? scope.id : undefined;
  const chainId = scope?.type === "chain" ? scope.id : undefined;
  const { data: store } = useGetStoreQuery(storeId);
  const hasFeature = useHasCatalogFeature(storeId ?? null, scope?.type === "chain");
  const chainIdForQuery = chainId ?? store?.chainId;
  const { data: chain, isError: isChainError } =
    useGetChainQuery(chainIdForQuery);
  const showCatalogOrigin = useShowCatalogOriginMarkers(scope);
  const { data: groupsData } = useGetProductGroupsQuery(scope);
  const { data: unitsData } = useGetUnitsQuery(scope);
  const { data: coursesData } = useGetCoursesQuery(scope);
  const { data: prepStationsData } = useGetPrepStationsQuery(scope);
  const { data: bookkeepingAccountsData } =
    useGetBookkeepingAccountsQuery(scope);
  const extraProductFields = store?.extraProductFields ?? null;
  const country = store?.country ?? chain?.country ?? Country.SWEDEN;
  const currency = store?.currency ?? Currency.SEK;
  const bookkeepingDefaultAccounts = store?.bookkeepingDefaultAccounts;

  const overlayStoreId = storeId && chain?.chainMode === true ? storeId : null;
  const { data: user, isError: isUserError } = useGetUserQuery();
  const { data: storePermissions, isError: isStorePermissionsError } =
    useGetStorePermissionsQuery(overlayStoreId);
  const { data: overlaysData, isError: isOverlaysError } =
    useGetProductOverlaysQuery(overlayStoreId);
  const canManageChainCatalog =
    !!store &&
    hasChainCatalogAccess(user, store) &&
    (storePermissions?.permissions.includes(Permissions.CHAIN_CATALOG) ?? false);
  const allowedOverlayFields = useMemo(() => {
    if (!overlayStoreId || !chain) {
      return new Set<ProductOverlayField>();
    }
    const policy = resolveDomainPolicy(
      chain.catalogPolicy ?? defaultCatalogPolicy,
      store?.catalogPolicyOverrides,
      CatalogDomain.PRODUCTS,
    );
    return new Set(
      ProductOverlayFieldScheme.options.filter((field) =>
        isFieldOverridable(policy, field),
      ),
    );
  }, [overlayStoreId, chain, store?.catalogPolicyOverrides]);
  const [selectedSaveTarget, setChainSaveTarget] =
    useState<ChainProductSaveTarget>("chain");
  const showChainSaveTarget =
    canManageChainCatalog && allowedOverlayFields.size > 0;
  const isOverlayReady =
    (!storeId || !!chain) &&
    (!overlayStoreId || (!!user && !!storePermissions && !!overlaysData));
  const isOverlayError =
    (!!storeId && isChainError) ||
    (!!overlayStoreId &&
      (isUserError || isStorePermissionsError || isOverlaysError));
  const chainSaveTarget: ChainProductSaveTarget = !overlayStoreId
    ? "chain"
    : !canManageChainCatalog
      ? "store"
      : allowedOverlayFields.size > 0
        ? selectedSaveTarget
        : "chain";
  const overlaysById = useMemo(
    () => new Map((overlaysData ?? []).map((item) => [item.productId, item])),
    [overlaysData],
  );

  const columnLabels = useMemo(
    () => buildProductColumnLabels(extraProductFields, hasFeature),
    [extraProductFields, hasFeature],
  );

  const productGroupsById = useMemo(() => {
    const map = new Map<ProductGroupId, RestProductGroup>();
    (groupsData ?? []).forEach((group) => {
      map.set(group.id, group);
    });
    return map;
  }, [groupsData]);

  const filters = useProductTableFilters({ columnLabels });

  const stockByLocationEnabled =
    !!storeId &&
    hasFeature([Features.STOCK_LOCATIONS]) &&
    hasFeature([Features.SIMPLE_STOCK]);
  const { data: stockLocationsData } = useGetStockLocationsQuery(
    stockByLocationEnabled ? (store?.chainId ?? null) : null,
  );
  const defaultStockLocationId = store?.stockLocationId ?? null;
  const [selectedStockLocationId, setStockLocationId] = useState<
    StockLocationId | null | undefined
  >(undefined);
  const stockLocationId = stockByLocationEnabled
    ? selectedStockLocationId === undefined
      ? defaultStockLocationId
      : selectedStockLocationId
    : null;

  const stockPoliciesEnabled =
    !!storeId &&
    hasFeature([Features.STOCK_POLICIES]) &&
    hasFeature([Features.SIMPLE_STOCK]);
  const stockWarningFilter = stockPoliciesEnabled
    ? filters.stockWarningFilter
    : null;
  const { data: stockWarningsData } = useGetStockWarningsQuery(
    stockWarningFilter !== null ? (storeId ?? null) : null,
    stockLocationId,
  );
  const baseQuery = useMemo(() => {
    const stockFields =
      stockLocationId === null
        ? {}
        : {
            stockFilter: null,
            ...(filters.baseQuery.sortColumn === "stockCount" && {
              sortColumn: "updatedAt" as const,
              sortDirection: "desc" as const,
            }),
          };
    if (stockWarningFilter === null) {
      return { ...filters.baseQuery, ...stockFields, productIds: null };
    }
    const types =
      stockWarningFilter === "out"
        ? [StockWarningType.NEGATIVE, StockWarningType.OUT_OF_STOCK]
        : [StockWarningType.BELOW_MIN];
    return {
      ...filters.baseQuery,
      ...stockFields,
      productIds: [
        ...new Set(
          (stockWarningsData ?? [])
            .filter((warning) => types.includes(warning.type))
            .map((warning) => warning.productId),
        ),
      ],
    };
  }, [filters.baseQuery, stockLocationId, stockWarningFilter, stockWarningsData]);

  const baseTableQuery = useProductTableQuery({
    scope,
    storeRef,
    status,
    version,
    baseQuery,
  });
  const query = useMemo(() => {
    if (!overlayStoreId || chainSaveTarget !== "chain" || overlaysById.size === 0) {
      return baseTableQuery;
    }
    const rows = baseTableQuery.rows.map((product) => {
      const item =
        product.origin === CatalogOrigin.CHAIN
          ? overlaysById.get(product.id)
          : undefined;
      return item ? applyChainValues(product, item.chainValues) : product;
    });
    return { ...baseTableQuery, rows };
  }, [baseTableQuery, overlayStoreId, chainSaveTarget, overlaysById]);

  const stock = useProductTableStock({
    storeId,
    stockLocationId,
    rows: query.rows,
  });
  const { data: suppliersData } = useGetSuppliersQuery(
    hasFeature([Features.SUPPLIERS]) ? scope : null,
  );
  const { data: stockPoliciesData } = useGetStockPoliciesQuery(
    stockPoliciesEnabled ? (store?.chainId ?? null) : null,
  );
  const stockPoliciesById = useMemo(
    () => new Map((stockPoliciesData ?? []).map((policy) => [policy.id, policy])),
    [stockPoliciesData],
  );
  const businessDate = useMemo(() => {
    if (!store) {
      return null;
    }
    const local = DateTime.now().setZone(store.timeZone);
    return (
      local.hour < (store.businessDayStartHour ?? 0)
        ? local.minus({ days: 1 })
        : local
    ).toFormat("yyyy-MM-dd");
  }, [store]);
  const stockStatus = useCallback<ProductTableMeta["stockStatus"]>(
    (quantity, assignment): StockStatus => {
      if (!storeId || quantity === null) {
        return null;
      }
      if (quantity <= 0) {
        return "out";
      }
      if (!stockPoliciesEnabled || !businessDate || !assignment) {
        return null;
      }
      const policy = assignment.policyId
        ? (stockPoliciesById.get(assignment.policyId) ?? null)
        : null;
      return evaluateStock(quantity, assignment, policy, businessDate)?.type ===
        StockWarningType.BELOW_MIN
        ? "low"
        : null;
    },
    [storeId, stockPoliciesEnabled, businessDate, stockPoliciesById],
  );

  const [expanded, setExpanded] = useState<{
    search: string;
    ids: ReadonlySet<ProductId>;
  }>({ search: "", ids: new Set() });
  const isSearching = filters.search !== "" || stockWarningFilter !== null;
  const expandAllKey = `${filters.search}|${stockWarningFilter ?? ""}`;
  const toggledIds = useMemo(
    () =>
      expanded.search === expandAllKey ? expanded.ids : new Set<ProductId>(),
    [expanded, expandAllKey],
  );
  const isExpanded = useCallback(
    (id: ProductId) => toggledIds.has(id) !== isSearching,
    [toggledIds, isSearching],
  );
  const toggleExpanded = useCallback(
    (id: ProductId) => {
      const ids = new Set(toggledIds);
      if (ids.has(id)) {
        ids.delete(id);
      } else {
        ids.add(id);
      }
      setExpanded({ search: expandAllKey, ids });
    },
    [toggledIds, expandAllKey],
  );

  const restrictChanges = useCallback(
    (product: RestProduct, changes: RestUpdateProduct): RestUpdateProduct =>
      restrictOverlayChanges(
        { saveTarget: chainSaveTarget, allowedFields: allowedOverlayFields },
        product,
        changes,
      ),
    [chainSaveTarget, allowedOverlayFields],
  );

  const edits = useProductTableEdits({
    scope,
    rows: query.rows,
    selectedIds: filters.selectedIds,
    productGroupsById,
    applyUpdated,
    setRowSelection: filters.setRowSelection,
    overlayStoreId,
    saveChainToOverlay: !!overlayStoreId && chainSaveTarget === "store",
    overlays: overlaysById,
    restrictChanges,
  });

  const { resetOverride, isResetting } = edits;
  const overlay = useMemo(
    (): ProductOverlayMeta | null =>
      overlayStoreId
        ? {
            saveTarget: chainSaveTarget,
            overlays: overlaysById,
            allowedFields: allowedOverlayFields,
            reset: (productId, field, variantId) =>
              void resetOverride(productId, field, variantId),
            isResetting,
          }
        : null,
    [
      overlayStoreId,
      chainSaveTarget,
      overlaysById,
      allowedOverlayFields,
      resetOverride,
      isResetting,
    ],
  );

  const groups = useMemo(
    () => (groupsData ?? []).map((group) => ({ id: group.id, name: group.name })),
    [groupsData],
  );

  useEffect(() => {
    const productStore = storeRef.current;
    if (!productStore || status !== "ready") {
      return;
    }
    void productStore.setGroups(groups).catch((error) => {
      console.error("[ProductsTableProvider] setGroups failed", error);
    });
  }, [groups, status, storeRef]);

  const units = useMemo(
    () => (unitsData ?? []).map((unit) => ({ id: unit.id, name: unit.name })),
    [unitsData],
  );
  const courses = useMemo(
    () => (coursesData ?? []).map((course) => ({ id: course.id, name: course.name })),
    [coursesData],
  );
  const prepStations = useMemo(
    () =>
      (prepStationsData ?? []).map((station) => ({
        id: station.id,
        name: station.name,
      })),
    [prepStationsData],
  );

  const columns = useMemo(
    () =>
      createProductColumns(
        extraProductFields,
        columnLabels,
        stockLocationId !== null,
      ),
    [extraProductFields, columnLabels, stockLocationId],
  );

  const productMeta: ProductTableMeta = useMemo(
    () => ({
      edits: edits.edits,
      setEdit: edits.setEdit,
      groups,
      productGroupsById,
      units,
      courses,
      prepStations,
      bookkeepingAccounts: bookkeepingAccountsData ?? [],
      country,
      bookkeepingDefaultAccounts,
      currency,
      onEditProduct,
      showCatalogOrigin,
      overlay,
      isExpanded,
      toggleExpanded,
      stock: stock.stock,
      stockStatus,
      t,
    }),
    [
      edits.edits,
      edits.setEdit,
      groups,
      productGroupsById,
      units,
      courses,
      prepStations,
      bookkeepingAccountsData,
      country,
      bookkeepingDefaultAccounts,
      currency,
      onEditProduct,
      showCatalogOrigin,
      overlay,
      isExpanded,
      toggleExpanded,
      stock.stock,
      stockStatus,
      t,
    ],
  );

  const tableMeta: TableMeta<RestProduct> = useMemo(
    () => ({ productMeta }),
    [productMeta],
  );

  const onDeleteSelected = useCallback(() => {
    const ids = filters.selectedIds;
    if (ids.length === 0) {
      return;
    }
    if (!window.confirm(t("productsView.bulkDeleteConfirm", { count: ids.length }))) {
      return;
    }
    const idSet = new Set(ids);
    const products = query.rows.filter((product) => idSet.has(product.id));
    void deleteProducts(products)
      .then(() => {
        filters.setRowSelection({});
      })
      .catch((error) => {
        console.error("[ProductsTableProvider] bulk delete failed", error);
      });
  }, [deleteProducts, filters, query.rows, t]);

  const value = useMemo(
    (): ProductsTableContextValue => ({
      canEdit,
      status,
      syncProgress,
      lastSyncedAt,
      resync,
      forceResync,
      toolbarActions,
      onDeleteSelected,
      columnLabels,
      columns,
      groups,
      hasFeature,
      currency,
      bookkeepingAccounts: bookkeepingAccountsData ?? [],
      tableMeta,
      filters,
      query,
      edits,
      stock,
      stockLocations: stockByLocationEnabled ? (stockLocationsData ?? []) : [],
      stockLocationId,
      defaultStockLocationId,
      setStockLocationId,
      showStockWarningFilter: stockPoliciesEnabled,
      showChainSaveTarget,
      chainSaveTarget,
      setChainSaveTarget,
      isOverlayReady,
      isOverlayError,
      suppliers: suppliersData ?? [],
      units,
    }),
    [
      canEdit,
      status,
      syncProgress,
      lastSyncedAt,
      resync,
      forceResync,
      toolbarActions,
      onDeleteSelected,
      columnLabels,
      columns,
      groups,
      hasFeature,
      currency,
      bookkeepingAccountsData,
      tableMeta,
      filters,
      query,
      edits,
      stock,
      stockByLocationEnabled,
      stockLocationsData,
      defaultStockLocationId,
      stockPoliciesEnabled,
      showChainSaveTarget,
      chainSaveTarget,
      isOverlayReady,
      isOverlayError,
      suppliersData,
      units,
      stockLocationId,
    ],
  );

  return (
    <ProductsTableContext.Provider value={value}>
      {children}
    </ProductsTableContext.Provider>
  );
};
